import { Alert, Anchor, Button, Group, Paper, Radio, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import { useCallback, useRef, useState } from 'react'
import { Link } from 'wouter'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useHasPublishedModule } from '@/hooks/useHasPublishedModule'
import { useTabNavigationWarning } from '@/hooks/useTabNavigationWarning'
import type { GetSponsorInfoResponse } from '@/services/api/api.types'
import { validationErrorMessage } from '@/utils/apiErrors'
import { getRoutePath } from '@/utils/navigation'
import { DEFAULT_GUEST_MESSAGE_LIMIT, getChannelRegistrationUrl } from '@/utils/sponsors'

import { AccessTypeRadios, ComingSoonLabel, LinkField, SettingCard } from './AccessControls'
import type { AccessSettings, GuestDestination } from './accessSettings'
import {
  canSave,
  fromSponsorInfo,
  isAccessTypeAvailable,
  isGuestDestinationAvailable,
  isSameSettings,
  missingUrls,
  readStoredSettings,
  setGuestAccess,
  setSelfRegEnabled,
  setTrialsEnabled,
  toUpdateParams,
} from './accessSettings'
import { copy } from './copy'
import { DeleteChannelCard } from './DeleteChannelCard'
import { WebSetupCard } from './WebSetupCard'

export const AccessSettingsPanel = observer(function AccessSettingsPanel() {
  const { api } = useGlobalState()

  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  if (!sponsorInfoQuery.data) {
    return (
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Group justify="space-between" align="center" p="md">
          <Title order={4}>{copy.manage.title}</Title>
          <Text>{sponsorInfoQuery.isError ? 'Error loading data' : 'Loading...'}</Text>
        </Group>
      </Paper>
    )
  }

  return <AccessSettingsForm sponsorInfo={sponsorInfoQuery.data} />
})

const AccessSettingsForm = observer(function AccessSettingsForm({
  sponsorInfo,
}: {
  sponsorInfo: GetSponsorInfoResponse
}) {
  const { api, authenticationStore } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const registrationUrl = getChannelRegistrationUrl(authenticationStore.user?.sponsorId)
  const hasPublishedModule = useHasPublishedModule()

  const [syncedInfo, setSyncedInfo] = useState(sponsorInfo)
  const [stored, setStored] = useState(() => readStoredSettings(sponsorInfo, registrationUrl))
  const [saved, setSaved] = useState(() => fromSponsorInfo(sponsorInfo, registrationUrl))
  const [draft, setDraft] = useState(saved)
  const [isSaving, setIsSaving] = useState(false)
  const [touchedUrls, setTouchedUrls] = useState({ paymentUrl: false, guestPaymentUrl: false })
  const [discoverBlocked, setDiscoverBlocked] = useState(false)
  const urlInputs = { paymentUrl: useRef<HTMLInputElement>(null), guestPaymentUrl: useRef<HTMLInputElement>(null) }

  const hasChanges = !isSameSettings(saved, draft)

  // A refetch replaces the saved values, and the draft too unless the owner is mid-edit.
  if (sponsorInfo !== syncedInfo) {
    setSyncedInfo(sponsorInfo)
    setStored(readStoredSettings(sponsorInfo, registrationUrl))
    const next = fromSponsorInfo(sponsorInfo, registrationUrl)
    setSaved(next)
    if (!hasChanges) setDraft(next)
  }

  const channelName = (sponsorInfo.name ?? authenticationStore.user?.sponsorName ?? '').trim()

  const discard = useCallback(() => {
    setDraft(saved)
    setTouchedUrls({ paymentUrl: false, guestPaymentUrl: false })
    setDiscoverBlocked(false)
  }, [saved])

  useTabNavigationWarning({
    hasUnsavedChanges: () => hasChanges,
    onDiscard: discard,
    message: 'You have unsaved access settings. Are you sure you want to leave this page?',
    title: 'Unsaved Access Settings',
  })

  const persist = async (next: AccessSettings) => {
    const params = toUpdateParams(stored, next, { registrationUrl })
    setIsSaving(true)
    try {
      await api.updateAccessSettings(params)
      setTouchedUrls({ paymentUrl: false, guestPaymentUrl: false })
      // Show what the backend kept, which leaves out anything it doesn't store yet.
      const fresh = await api.queryClient
        .fetchQuery({ queryKey: ['sponsor', 'info'], queryFn: () => api.getSponsorInfo(), staleTime: 0 })
        .catch(() => null)
      if (fresh) {
        setSyncedInfo(fresh)
        setStored(readStoredSettings(fresh, registrationUrl))
        const display = fromSponsorInfo(fresh, registrationUrl)
        setSaved(display)
        setDraft(display)
      } else {
        const normalized = { ...next, paymentUrl: next.paymentUrl.trim(), guestPaymentUrl: next.guestPaymentUrl.trim() }
        const destinationSent = params.registration_url_use_trial !== undefined
        setStored({ ...normalized, guestDestinationUnset: stored.guestDestinationUnset && !destinationSent })
        setSaved(normalized)
        setDraft(normalized)
      }
      notifications.show({
        title: 'Settings Saved',
        message: 'Your channel access settings have been updated.',
        color: 'green',
      })
    } catch (error) {
      console.error('Error saving access settings:', error)
      notifications.show({
        title: 'Save Failed',
        message: validationErrorMessage(error) ?? 'Could not save access settings. Please try again.',
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (!hasChanges) return
    modals.openConfirmModal({
      title: 'Discard Changes',
      centered: true,
      children: 'You have unsaved changes. Are you sure you want to discard them?',
      labels: { confirm: 'Discard', cancel: 'Keep Editing' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: discard,
    })
  }

  const handleDiscoverableChange = (on: boolean) => {
    const allowed = !on || !!hasPublishedModule
    setDiscoverBlocked(!allowed)
    if (allowed) setDraft(current => ({ ...current, discoverable: on }))
  }

  const urlError = (key: 'paymentUrl' | 'guestPaymentUrl') => {
    const shown = touchedUrls[key] || draft[key].trim() !== ''
    return shown && missingUrls(draft, saved).includes(key) ? copy.trials.invalidUrl : undefined
  }
  const touchUrl = (key: 'paymentUrl' | 'guestPaymentUrl') => setTouchedUrls(current => ({ ...current, [key]: true }))

  const blockingUrl = hasChanges ? missingUrls(draft, saved)[0] : undefined
  const focusUrl = (key: 'paymentUrl' | 'guestPaymentUrl') => {
    const input = urlInputs[key].current
    input?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    input?.focus({ preventScroll: true })
  }

  const guestLimit = sponsorInfo.guestMessageLimit ?? DEFAULT_GUEST_MESSAGE_LIMIT
  const guestRadio = (destination: GuestDestination, label: string, description: string, unavailable: string) => {
    const available = isGuestDestinationAvailable(draft, destination)
    return (
      <Radio
        value={destination}
        label={label}
        description={available ? description : unavailable}
        disabled={isSaving || !available}
      />
    )
  }

  return (
    <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
      <Group justify="space-between" align="flex-start" mb="lg" wrap={isMobile ? 'wrap' : 'nowrap'}>
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Title order={isMobile ? 6 : 4}>{copy.manage.title}</Title>
          <Text size="sm" c="dimmed">
            {copy.manage.description}
          </Text>
        </Stack>
        <Group justify="flex-end" gap="sm">
          {blockingUrl && (
            <UnstyledButton
              onClick={() => focusUrl(blockingUrl)}
              fz="sm"
              fw={600}
              c="red"
              style={{ whiteSpace: 'nowrap' }}>
              {copy.saveHints[blockingUrl]}
            </UnstyledButton>
          )}
          <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => persist(draft)}
            disabled={!hasChanges || !canSave(draft, saved) || isSaving}
            loading={isSaving}>
            Save
          </Button>
        </Group>
      </Group>

      <Stack gap="lg">
        <SettingCard
          title={copy.trials.title}
          description={copy.trials.description}
          checked={draft.trialsEnabled}
          onChange={on => setDraft(current => setTrialsEnabled(current, on))}
          disabled={isSaving}>
          {draft.trialsEnabled && (
            <Radio.Group value="own" aria-label="Payment page">
              <Stack gap="md">
                <Radio value="own" label={copy.trials.ownPage} description={copy.trials.ownPageDescription} />
                <Stack gap={0} pl={isMobile ? 0 : 32}>
                  <LinkField
                    value={draft.paymentUrl}
                    label={copy.trials.urlLabel}
                    onChange={paymentUrl => setDraft(current => ({ ...current, paymentUrl }))}
                    onBlur={() => touchUrl('paymentUrl')}
                    placeholder={copy.trials.urlPlaceholder}
                    error={urlError('paymentUrl')}
                    disabled={isSaving}
                    inputRef={urlInputs.paymentUrl}
                  />
                </Stack>
                <Radio
                  value="sidekick"
                  label={<ComingSoonLabel label={copy.trials.sidekickCheckout} />}
                  description={copy.trials.sidekickCheckoutDescription}
                  disabled
                />
              </Stack>
            </Radio.Group>
          )}
        </SettingCard>

        <Stack gap={4} mt="sm">
          <Title order={isMobile ? 5 : 4}>{copy.registration.title}</Title>
          <Text size="sm" c="dimmed">
            {copy.registration.description}
          </Text>
        </Stack>

        <SettingCard title={copy.manualInvites.title} description={copy.manualInvites.description}>
          <AccessTypeRadios
            kind="invite"
            label={copy.manualInvites.title}
            value={draft.inviteDefaultAccess}
            onChange={inviteDefaultAccess => setDraft(current => ({ ...current, inviteDefaultAccess }))}
            isAvailable={type => isAccessTypeAvailable(draft, 'invite', type)}
            disabled={isSaving}
          />
        </SettingCard>

        <SettingCard
          title={copy.selfReg.title}
          description={copy.selfReg.description}
          checked={draft.selfRegEnabled}
          onChange={on => {
            setDraft(current => setSelfRegEnabled(current, on))
            if (!on) setDiscoverBlocked(false)
          }}
          disabled={isSaving}>
          {draft.selfRegEnabled && (
            <Paper radius="md" p={isMobile ? 'md' : 'lg'} withBorder>
              <Stack gap="md">
                <Stack gap={4}>
                  <Text fw={600}>{copy.selfReg.accessTitle}</Text>
                  <Text size="sm" c="dimmed">
                    {copy.selfReg.accessDescription}
                  </Text>
                </Stack>
                <AccessTypeRadios
                  kind="selfReg"
                  label={copy.selfReg.accessTitle}
                  value={draft.selfRegAccess}
                  onChange={selfRegAccess => setDraft(current => ({ ...current, selfRegAccess }))}
                  isAvailable={type => isAccessTypeAvailable(draft, 'selfReg', type)}
                  disabled={isSaving}
                />
                {registrationUrl && (
                  <Stack gap="xs">
                    <Text size="sm">{copy.selfReg.shareLink}</Text>
                    <LinkField value={registrationUrl} label="registration URL" />
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}
        </SettingCard>

        <SettingCard
          title={copy.discoverable.title}
          description={
            <>
              {copy.discoverable.description}
              {!draft.selfRegEnabled && (
                <Text span display="block" size="sm" c="var(--mantine-color-text)">
                  {copy.discoverable.needsSelfReg}
                </Text>
              )}
            </>
          }
          checked={draft.discoverable}
          onChange={handleDiscoverableChange}
          disabled={isSaving || hasPublishedModule === undefined || !draft.selfRegEnabled}>
          {discoverBlocked && (
            <Alert
              variant="light"
              color="orange"
              icon={<IconAlertTriangle size={18} />}
              title={copy.discoverable.blockedTitle}>
              <Text size="sm">
                {copy.discoverable.blockedBody}{' '}
                <Anchor component={Link} to={getRoutePath('contentModules')} size="sm" fw={600}>
                  {copy.discoverable.blockedLink}
                </Anchor>
              </Text>
            </Alert>
          )}
        </SettingCard>

        <SettingCard
          title={copy.guest.title}
          description={copy.guest.description(guestLimit)}
          checked={draft.guestAccess}
          onChange={on => setDraft(current => setGuestAccess(current, on, saved))}
          disabled={isSaving}>
          {draft.guestAccess && (
            <Paper radius="md" p={isMobile ? 'md' : 'lg'} withBorder>
              <Stack gap="md">
                <Stack gap={4}>
                  <Text fw={600}>{copy.guest.destinationTitle}</Text>
                  <Text size="sm" c="dimmed">
                    {copy.guest.destinationDescription}
                  </Text>
                </Stack>
                <Radio.Group
                  value={draft.guestDestination}
                  onChange={value => setDraft(current => ({ ...current, guestDestination: value as GuestDestination }))}
                  aria-label={copy.guest.destinationTitle}>
                  <Stack gap="md">
                    {guestRadio(
                      'trial',
                      copy.guest.trialPage,
                      copy.guest.trialPageDescription,
                      copy.guest.trialPageUnavailable,
                    )}
                    {guestRadio('external', copy.guest.otherPage, copy.guest.otherPageDescription, '')}
                    {draft.guestDestination === 'external' && (
                      <Stack gap={0} pl={isMobile ? 0 : 32}>
                        <LinkField
                          value={draft.guestPaymentUrl}
                          label={copy.guest.urlLabel}
                          onChange={guestPaymentUrl => setDraft(current => ({ ...current, guestPaymentUrl }))}
                          onBlur={() => touchUrl('guestPaymentUrl')}
                          placeholder={copy.trials.urlPlaceholder}
                          error={urlError('guestPaymentUrl')}
                          disabled={isSaving}
                          inputRef={urlInputs.guestPaymentUrl}
                        />
                      </Stack>
                    )}
                    {guestRadio(
                      'register',
                      copy.guest.registerForm,
                      copy.guest.registerFormDescription(draft.trialsEnabled && draft.selfRegAccess === 'trial'),
                      copy.guest.registerFormUnavailable,
                    )}
                    {draft.guestDestination === 'register' && registrationUrl && (
                      <Stack gap={0} pl={isMobile ? 0 : 32}>
                        <LinkField value={registrationUrl} label="guest registration URL" />
                      </Stack>
                    )}
                  </Stack>
                </Radio.Group>
              </Stack>
            </Paper>
          )}
        </SettingCard>

        <WebSetupCard />

        <DeleteChannelCard channelName={channelName} disabled={isSaving} />
      </Stack>
    </Paper>
  )
})
